import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';

import AutoFocusInput from '@stubhub/react-autofocus-input';
import DropdownList from '@stubhub/react-dropdown-list';
import {connect} from '@stubhub/react-store-provider';
import withQueryParams from '@stubhub/react-with-query-params';

import controller from './controller';
import messages from './messages';

import '../styles/index.scss';

const GeoFilter = (
  {
    className,
    items,
    currentGeo,
    onGeoSearchTextChange,
    dropdownAlign,
    useQueryParams = true,
    query,
    replaceQuery,
    preSelect,
    useBackdrop,
    draggable,
    onSelection,
    dropDownToggle = true,
    showDraggableTitle,
  },
  context
) => {
  const [isLoading, setIsLoading] = useState(false);
  const intl = useIntl();
  const {cookies, globalRegistry} = context;

  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      const queryParamsToggle = !!globalRegistry.getFeatureValue('common.queryParams');
      const options = query || {};
      const useQueryParamsToggle = queryParamsToggle && useQueryParams;

      if (useQueryParamsToggle && options.pbG) {
        preSelect(options.pbG, cookies);
      }
    }
  }, [cookies, globalRegistry, preSelect, query, useQueryParams]);

  const currentLocation = {
    key: '$0',
    name: intl.formatMessage(messages.curLoc),
  };
  const loadingLocation = {
    key: 'loading',
    name: intl.formatMessage(messages.loading),
  };
  const placeholderText = intl.formatMessage(messages.placeholder);
  const allLocText = intl.formatMessage(messages.allLoc);

  const handleSelect = (selectedLocation) => {
    if (selectedLocation) {
      const selectionResult = onSelection(selectedLocation, cookies);
      if (selectionResult && selectionResult.then) {
        setIsLoading(true);

        return selectionResult
          .then((locationObj) => {
            setIsLoading(false);
            const {key, name} = selectedLocation;
            const activeLocation =
              key === '$0'
                ? {
                    key,
                    name: (locationObj && locationObj.name) || name,
                    index: 0,
                  }
                : selectedLocation;
            useQueryParams && replaceQuery({pbG: activeLocation.name});

            return locationObj;
          })
          .catch(() => {
            setIsLoading(false);
          });
      }
      useQueryParams && replaceQuery({pbG: selectedLocation.name});

      return selectionResult;
    }
  };

  const searchBox = (
    <AutoFocusInput className="geo-input" placeholder={placeholderText} onChange={onGeoSearchTextChange} />
  );

  const combinedItems = [currentLocation, ...items];

  const currentGeoLocation = isLoading ? loadingLocation : currentGeo || {name: allLocText};

  return (
    <div className={className}>
      <DropdownList
        items={combinedItems}
        selectionTitle={searchBox}
        onSelection={handleSelect}
        selected={currentGeoLocation}
        iconCls="geo-location-icon__merch"
        className="MerchHeader"
        dropDownToggle={dropDownToggle}
        dropdownAlign={dropdownAlign}
        draggable={draggable}
        useBackdrop={useBackdrop}
        showDraggableTitle={showDraggableTitle}
      />
    </div>
  );
};

GeoFilter.contextTypes = {
  // The cookies object.
  cookies: PropTypes.object,
  // The global registry object.
  globalRegistry: PropTypes.object,
};

GeoFilter.propTypes = {
  // The CSS class name for the component.
  className: PropTypes.string,
  // The callback function for when the geo search text changes.
  onGeoSearchTextChange: PropTypes.func.isRequired,
  // The callback function for pre-selecting a location.
  preSelect: PropTypes.func.isRequired,
  // The callback function for replacing the query.
  replaceQuery: PropTypes.func,
  // Flag indicating whether to use query parameters.
  useQueryParams: PropTypes.bool,
  // The list of location items.
  items: PropTypes.arrayOf(PropTypes.object),
  // The currently selected location.
  currentGeo: PropTypes.object,
  // The alignment of the dropdown.
  dropdownAlign: PropTypes.string,
  // The query object.
  query: PropTypes.object,
  // The callback function for when a location is selected.
  onSelection: PropTypes.func,
  // Flag indicating whether to use a backdrop.
  useBackdrop: PropTypes.bool,
  // Flag indicating whether the dropdown is draggable.
  draggable: PropTypes.bool,
  // Flag indicating whether the dropdown toggle is enabled.
  dropDownToggle: PropTypes.bool,
  // Flag indicating whether to show the draggable title.
  showDraggableTitle: PropTypes.bool,
};

export const WithoutQueryParams = connect(controller)(GeoFilter);
export default connect(controller)(withQueryParams({pbG: 'pbG'})(GeoFilter));
export {default as messages} from './messages';
